/**
 * Real-Time GEX workspace — the platform's own chart, styled and laid out like the MMEC
 * TradingView overlay: TradingView-style candles, MMEC level lines with the tool's exact
 * labels and colours, Regime & IV panel, 0DTE flow list, per-strike profiles, an IV / RV
 * pane and a control rail.
 *
 * Levels come from the overlay's own engine (vendor/mmecOverlay/processor.js, copied
 * verbatim), fed the same rows the desktop tool feeds it (services/liveGexService), so the
 * numbers match the tool. If the chain's price stamp is stale the levels are withheld.
 */
import React, { Component, ReactNode, useEffect, useRef, useState } from "react";
import {
    CandlestickSeries,
    ColorType,
    LineSeries,
    LineStyle,
    createChart,
    createSeriesMarkers,
    IChartApi,
    IPriceLine,
    ISeriesApi,
    Time,
} from "lightweight-charts";
import { processRowsByDTE } from "../../vendor/mmecOverlay/processor";
import {
    attachAccumulatedVolume,
    feedSymbol,
    fetchBars,
    fetchChain,
    fetchIvHistory,
    fetchIvSnapshot,
    fetchRtlRows,
    fetchZeroDteFlow,
    rtlNetByStrike,
    spotCheck,
    ChainResult,
    FlowPrint,
    LiveGexRange,
    RawBar,
    SpotCheck,
} from "../../services/liveGexService";
import { LevelLine, LiveGexPrimitive, StrikeBar } from "./LiveGexPrimitive";
import { analyse, TaResult } from "./autoTa";
import "./LiveGexWorkspace.css";
import "./LiveGexWorkspace.ds.css";
import { isDesignSystemOn } from "../../theme/mmecDsPreview";
import { ToolState } from "../mmec/ToolState";

const BG = "#0b0d14";
/* Design System preview (?ds=1) only; hexes from theme/mmec-ds/tokens.css. lightweight-charts and the
   canvas layer cannot read CSS variables, so the colours are picked here. */
const DS_BG = "#161a25"; // --ground-pane
const chartBg = (): string => (isDesignSystemOn() ? DS_BG : BG);
/** Levels: four hues x line style (designer answer 1). Walls magenta (CW solid, PW dashed), gamma levels
 *  blue (GF / ZG solid, VGF dashed), volatility orange (VMAX solid, VMIN dashed). */
const DS_LEVEL: Record<string, { color: string; solid: boolean }> = {
    GF: { color: "#2962ff", solid: true },
    ZG: { color: "#2962ff", solid: true },
    CW: { color: "#e040fb", solid: true },
    PW: { color: "#e040fb", solid: false },
    VGF: { color: "#2962ff", solid: false },
    VMAX: { color: "#ffa726", solid: true },
    VMIN: { color: "#ffa726", solid: false },
};
const DS_REGIME: Record<string, { color: string; bg: string; ribbon: string }> = {
    positive: { color: "#4dd0c4", bg: "#26a69a33", ribbon: "#26a69a2e" },
    negative: { color: "#ffb74d", bg: "#ffb74d1f", ribbon: "#ffb74d24" },
};
const IV_PANE_H = 150;
const POLL_MS = 40000;
const SYMBOLS = ["SPX", "NDX", "SPY", "QQQ", "IWM", "NVDA", "TSLA", "GLD", "SLV", "IBIT"];
const NAMES: Record<string, string> = {
    SPX: "S&P 500",
    NDX: "Nasdaq 100",
    SPY: "SPDR S&P 500 ETF",
    QQQ: "Invesco QQQ",
    IWM: "iShares Russell 2000",
    NVDA: "NVIDIA",
    TSLA: "Tesla",
    GLD: "SPDR Gold Shares",
    SLV: "iShares Silver Trust",
    IBIT: "iShares Bitcoin Trust",
};
/** Overlay palette (its drawings.js): OI levels take the DTE colour, volume levels are cyan. */
const DTE_COLORS: Record<number, string> = { 0: "#e879f9", 1: "#c084fc", 2: "#a78bfa", 3: "#818cf8", 7: "#34d399", 14: "#fbbf24", 30: "#94a3b8" };
const DTE_ALL_COLOR = "#c4b5fd";
/** Same rule as the overlay's dteColorFor: snap to the nearest bucket the palette defines. */
function dteColor(dte: number | null): string {
    if (dte == null) return DTE_ALL_COLOR;
    const keys = [0, 1, 2, 3, 7, 14, 30];
    const k = keys.reduce((p, c) => (Math.abs(c - dte) < Math.abs(p - dte) ? c : p), 0);
    return DTE_COLORS[k];
}
const VOL_COLOR = { VGF: "#22d3ee", VMAX: "#06b6d4", VMIN: "#0891b2" };
/** Overlay regime table (its ivModel.js). */
const REGIMES: Record<string, { title: string; short: string; who: string; hint: string; color: string; bg: string; ribbon: string }> = {
    positive: {
        title: "+γ POSITIVE GAMMA",
        short: "+γ PINNED",
        who: "Dealers long gamma",
        hint: "Dealers long gamma: moves get damped. Fade extremes toward the walls; expect pinning.",
        color: "#93c5fd",
        bg: "rgba(59,130,246,0.18)",
        ribbon: "rgba(59,130,246,0.22)",
    },
    negative: {
        title: "−γ NEGATIVE GAMMA",
        short: "−γ TRENDING",
        who: "Dealers short gamma",
        hint: "Dealers short gamma: moves extend. Respect breakouts; widen stops.",
        color: "#fdba74",
        bg: "rgba(249,115,22,0.18)",
        ribbon: "rgba(249,115,22,0.24)",
    },
};

function ensureInter(): void {
    const id = "lgx-inter-font";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap";
    document.head.appendChild(link);
}

/** Seconds to add to a UTC timestamp so the chart's (UTC) axis reads New York wall time. */
function nyOffsetSec(now: Date = new Date()): number {
    const ny = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
    const utc = new Date(now.toLocaleString("en-US", { timeZone: "UTC" }));
    return Math.round((ny.getTime() - utc.getTime()) / 1000);
}

const fmt = (v: any, nd = 2): string => (v == null || !Number.isFinite(Number(v)) ? "—" : Number(v).toFixed(nd));
const fmtPx = (v: number): string => v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function buildLines(lv: any, oi: string, spot: number): LevelLine[] {
    const order: Array<[string, string, string, boolean, number]> = [
        ["GF", "gammaFlip", oi, true, 2],
        ["ZG", "zeroGamma", oi, false, 1],
        ["CW", "callWall", oi, false, 1],
        ["PW", "putWall", oi, false, 1],
        ["VGF", "volGammaFlip", VOL_COLOR.VGF, false, 1],
        ["VMAX", "volMaxGex", VOL_COLOR.VMAX, false, 1],
        ["VMIN", "volMinGex", VOL_COLOR.VMIN, false, 1],
    ];
    const byPrice = new Map<number, LevelLine>();
    const ds = isDesignSystemOn();
    for (const [abbr, key, color0, solid0, width0] of order) {
        const sty = ds ? DS_LEVEL[abbr] : undefined;
        const color = sty ? sty.color : color0;
        const solid = sty ? sty.solid : solid0;
        const width = ds ? 2 : width0;
        const v = lv?.[key];
        if (v == null || !Number.isFinite(Number(v))) continue;
        const price = Math.round(Number(v) * 100) / 100;
        if (spot > 0 && Math.abs(price - spot) / spot > 0.015) continue; // keep the scale readable
        const hit = byPrice.get(price);
        if (hit) hit.abbr.push(abbr); // equal prices share one line: "GF · CW"
        else byPrice.set(price, { price, abbr: [abbr], color, solid, width });
    }
    return Array.from(byPrice.values()).sort((a, b) => b.price - a.price);
}

export interface LiveGexWorkspaceProps {
    symbol?: string;
    /** Fill the parent (embed / capture) instead of sizing to the app canvas. */
    fill?: boolean;
    /** Start on this expiry range (a stock has no 0DTE most days). */
    initialRange?: LiveGexRange;
    /** While the US market is closed, draw the last session's levels instead of withholding
     *  them: before the open the chain's quotes are yesterday's and can never match a
     *  pre-market price, so the stale-price guard would otherwise hide them all morning.
     *  During market hours the guard is unchanged. */
    sessionLevels?: boolean;
    /** Technical overlays on the candles: 20- and 50-bar averages. */
    ta?: boolean;
    /** Extra labelled price lines (a target, an expected range) drawn with the levels. */
    marks?: LiveGexMark[];
}

export interface LiveGexMark {
    label: string;
    price: number;
    color: string;
}

/** US cash session, New York time. */
function usMarketOpen(now: Date = new Date()): boolean {
    const p = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", weekday: "short", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(now);
    const get = (t: string) => p.find((x) => x.type === t)?.value || "";
    if (["Sat", "Sun"].includes(get("weekday"))) return false;
    const m = (Number(get("hour")) % 24) * 60 + Number(get("minute"));
    return m >= 9 * 60 + 30 && m < 16 * 60;
}

interface Legend {
    o: number;
    h: number;
    l: number;
    c: number;
    chg: number;
    chgPct: number;
}

const LiveGexWorkspace: React.FC<LiveGexWorkspaceProps> = ({
    symbol: initial = "SPX",
    fill = false,
    initialRange = "0dte",
    sessionLevels = false,
    ta = false,
    marks: marksProp,
}) => {
    const [symbol, setSymbol] = useState(String(initial).toUpperCase());
    const [range, setRange] = useState<LiveGexRange>(initialRange);
    const [lastSession, setLastSession] = useState(false);
    // Marks can also be handed in after load (a capture that computes them while the page
    // warms up): window.__lgxSetMarks([...]).
    const [marks, setMarks] = useState<LiveGexMark[]>(marksProp || []);
    const marksRef = useRef<LiveGexMark[]>(marksProp || []);
    const markLinesRef = useRef<IPriceLine[]>([]);
    const sma20Ref = useRef<ISeriesApi<"Line"> | null>(null);
    const sma50Ref = useRef<ISeriesApi<"Line"> | null>(null);
    const spotRef = useRef(0);
    // automatic technical reading (ta mode): trend lines, pattern, candlesticks, volume profile
    const [taRes, setTaRes] = useState<TaResult | null>(null);
    const [dex, setDex] = useState<number | null>(null);
    const [vpBars, setVpBars] = useState<{ top: number; h: number; w: number; poc: boolean; va: boolean }[]>([]);
    const trendRefs = useRef<ISeriesApi<"Line">[]>([]);
    const candleMarksRef = useRef<any>(null);
    const vpLinesRef = useRef<IPriceLine[]>([]);
    const [align, setAlign] = useState<"L" | "M" | "R">("M");
    const [showProfiles, setShowProfiles] = useState(true);
    const [showPanels, setShowPanels] = useState(true);
    const [snap, setSnap] = useState<any>(null);
    const [flow, setFlow] = useState<FlowPrint[]>([]);
    const [guard, setGuard] = useState<SpotCheck | null>(null);
    const [hasLevels, setHasLevels] = useState(false);
    const [dteLabel, setDteLabel] = useState("0DTE");
    const [legend, setLegend] = useState<Legend | null>(null);
    const [status, setStatus] = useState<"loading" | "live" | "error">("loading");
    const [stamp, setStamp] = useState("");
    const [dims, setDims] = useState({ w: 0, plotW: 0, mainH: 0 });
    // display-only copy of the drawn level lines for the Design System legend card (?ds=1)
    const [levelKey, setLevelKey] = useState<LevelLine[]>([]);

    const boxRef = useRef<HTMLDivElement | null>(null);
    const chartRef = useRef<IChartApi | null>(null);
    const candleRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
    const ivRef = useRef<ISeriesApi<"Line"> | null>(null);
    const rvRef = useRef<ISeriesApi<"Line"> | null>(null);
    const primRef = useRef<LiveGexPrimitive | null>(null);
    const priceLinesRef = useRef<IPriceLine[]>([]);
    const levelRangeRef = useRef<{ lo: number; hi: number } | null>(null);
    const barTimesRef = useRef<Set<number>>(new Set());
    const firstFitRef = useRef(true);

    useEffect(() => {
        if (!fill) return;
        (window as any).__lgxSetMarks = (m: LiveGexMark[]) => setMarks(Array.isArray(m) ? m : []);
        return () => {
            delete (window as any).__lgxSetMarks;
        };
    }, [fill]);

    // Marks: labelled lines beside the levels. Only those near the price are drawn, so a
    // far-away target does not squash the candles.
    useEffect(() => {
        marksRef.current = marks;
        const cs = candleRef.current;
        if (!cs) return;
        for (const pl of markLinesRef.current) {
            try {
                cs.removePriceLine(pl);
            } catch {
                /* already gone */
            }
        }
        markLinesRef.current = [];
        const spot = spotRef.current;
        for (const m of marks) {
            if (!(m.price > 0) || (spot > 0 && Math.abs(m.price / spot - 1) > 0.12)) continue;
            markLinesRef.current.push(
                cs.createPriceLine({ price: m.price, color: m.color, lineWidth: 2, lineStyle: LineStyle.Dotted, axisLabelVisible: true, title: m.label }),
            );
        }
        const near = marks.filter((m) => m.price > 0 && spot > 0 && Math.abs(m.price / spot - 1) <= 0.06).map((m) => m.price);
        if (near.length) {
            const lr = levelRangeRef.current;
            levelRangeRef.current = { lo: Math.min(...near, lr ? lr.lo : Infinity), hi: Math.max(...near, lr ? lr.hi : -Infinity) };
            chartRef.current?.priceScale("right").applyOptions({});
        }
    }, [marks, hasLevels, lastSession]);

    // Volume profile bars follow the price scale, so they are laid out from it after each
    // draw (and again once the scale has settled).
    useEffect(() => {
        if (!ta || !taRes?.profile) {
            setVpBars([]);
            return;
        }
        const place = () => {
            const cs = candleRef.current;
            const prof = taRes.profile;
            if (!cs || !prof) return;
            const max = Math.max(...prof.bins.map((b) => b.volume)) || 1;
            const out: { top: number; h: number; w: number; poc: boolean; va: boolean }[] = [];
            for (const b of prof.bins) {
                const y1 = cs.priceToCoordinate(b.hi);
                const y2 = cs.priceToCoordinate(b.lo);
                if (y1 == null || y2 == null || y2 > dims.mainH) continue;
                const mid = (b.lo + b.hi) / 2;
                out.push({
                    top: Math.min(y1, y2) + 0.5,
                    h: Math.max(1, Math.abs(y2 - y1) - 1),
                    w: Math.max(2, (b.volume / max) * 150),
                    poc: Math.abs(mid - prof.poc) < (b.hi - b.lo) / 2,
                    va: mid >= prof.val && mid <= prof.vah,
                });
            }
            setVpBars(out);
        };
        place();
        const t = window.setTimeout(place, 700);
        return () => window.clearTimeout(t);
    }, [ta, taRes, dims.mainH, dims.w, marks, hasLevels]);

    // ── chart (created once) ─────────────────────────────────────────
    useEffect(() => {
        const el = boxRef.current;
        if (!el) return;
        ensureInter();
        const dsOn = isDesignSystemOn();
        const chart = createChart(el, {
            width: el.clientWidth || 900,
            height: el.clientHeight || 520,
            layout: {
                background: { type: ColorType.Solid, color: chartBg() },
                textColor: dsOn ? "#b2b5be" : "#9aa4b5",
                fontFamily: dsOn ? "'JetBrains Mono', 'SF Mono', Consolas, monospace" : 'Inter, "Trebuchet MS", sans-serif',
                fontSize: dsOn ? 11 : 12,
                panes: { separatorColor: dsOn ? "#2a2e39" : "#1e2130", enableResize: false },
            },
            grid: dsOn
                ? { vertLines: { color: "rgba(42,46,57,0.5)" }, horzLines: { color: "rgba(42,46,57,0.5)" } }
                : { vertLines: { color: "rgba(42,46,57,0.28)" }, horzLines: { color: "rgba(42,46,57,0.28)" } },
            rightPriceScale: { borderColor: dsOn ? "#2a2e39" : "#1e2130", scaleMargins: { top: 0.1, bottom: 0.06 } },
            timeScale: { borderColor: dsOn ? "#2a2e39" : "#1e2130", timeVisible: true, secondsVisible: false, rightOffset: 26 },
        } as any);
        chartRef.current = chart;

        const cs = chart.addSeries(CandlestickSeries, {
            upColor: "#26a69a",
            downColor: "#ef5350",
            wickUpColor: "#26a69a",
            wickDownColor: "#ef5350",
            borderVisible: false,
            priceLineColor: "#26a69a",
            priceLineStyle: LineStyle.Dotted,
            // keep the nearby MMEC levels on screen even when price has not reached them
            autoscaleInfoProvider: (original: () => any) => {
                const r = original();
                const lr = levelRangeRef.current;
                if (!r || !lr) return r;
                return {
                    ...r,
                    priceRange: {
                        minValue: Math.min(r.priceRange.minValue, lr.lo),
                        maxValue: Math.max(r.priceRange.maxValue, lr.hi),
                    },
                };
            },
        } as any) as ISeriesApi<"Candlestick">;
        candleRef.current = cs;
        if (ta) {
            const line = (color: string) =>
                chart.addSeries(LineSeries, { color, lineWidth: 2, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false }, 0);
            sma20Ref.current = line("#38bdf8");
            sma50Ref.current = line("#c084fc");
            trendRefs.current = [0, 1].map(() =>
                chart.addSeries(LineSeries, { color: "#f87171", lineWidth: 2, lineStyle: LineStyle.Solid, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false }, 0),
            );
            candleMarksRef.current = createSeriesMarkers(cs as any, []);
        }
        const prim = new LiveGexPrimitive();
        primRef.current = prim;
        cs.attachPrimitive(prim);

        ivRef.current = (chart as any).addSeries(
            LineSeries,
            { color: dsOn ? "#ce93d8" : "#c084fc", lineWidth: 2, priceLineVisible: false, lastValueVisible: true },
            1,
        );
        rvRef.current = (chart as any).addSeries(
            LineSeries,
            { color: dsOn ? "#80deea" : "#fbbf24", lineWidth: 1, lineStyle: LineStyle.Dashed, priceLineVisible: false, lastValueVisible: false },
            1,
        );

        const measure = () => {
            const c = chartRef.current;
            if (!c || !boxRef.current) return;
            const panes: any[] = (c as any).panes?.() || [];
            try {
                panes[1]?.setHeight?.(IV_PANE_H);
            } catch {
                /* pane not laid out yet */
            }
            const h = panes[0]?.getHeight?.();
            setDims({
                w: boxRef.current.clientWidth,
                plotW: c.timeScale().width(),
                mainH: typeof h === "number" && h > 0 ? h : Math.max(0, boxRef.current.clientHeight - IV_PANE_H - 28),
            });
        };
        const ro = new ResizeObserver(() => {
            if (!chartRef.current || !boxRef.current) return;
            chartRef.current.applyOptions({ width: boxRef.current.clientWidth, height: boxRef.current.clientHeight });
            window.requestAnimationFrame(measure);
        });
        ro.observe(el);
        const raf = window.requestAnimationFrame(measure);
        const settle = window.setTimeout(measure, 600);

        return () => {
            window.cancelAnimationFrame(raf);
            window.clearTimeout(settle);
            ro.disconnect();
            priceLinesRef.current = [];
            primRef.current = null;
            candleRef.current = null;
            ivRef.current = null;
            rvRef.current = null;
            chart.remove();
            chartRef.current = null;
        };
    }, []);

    // ── on-canvas layer options ──────────────────────────────────────
    useEffect(() => {
        // dteShort is set with the levels themselves: it depends on the expiry the chain returned
        primRef.current?.setOptions({ symbol, align, showProfiles, background: chartBg() });
    }, [symbol, align, showProfiles]);

    // ── data loop ────────────────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;
        let timer = 0;
        let cycle = 0;
        let ivHist: any = null;
        firstFitRef.current = true;
        setStatus("loading");
        setSnap(null);
        setFlow([]);
        setGuard(null);
        setHasLevels(false);
        setLegend(null);
        const feed = feedSymbol(symbol);
        const off = nyOffsetSec();

        const clearLevels = () => {
            const cs = candleRef.current;
            for (const pl of priceLinesRef.current) {
                try {
                    cs?.removePriceLine(pl);
                } catch {
                    /* already gone */
                }
            }
            priceLinesRef.current = [];
            levelRangeRef.current = null;
            primRef.current?.setData([], []);
            setLevelKey([]);
        };
        clearLevels();
        candleRef.current?.setData([]);
        ivRef.current?.setData([]);
        rvRef.current?.setData([]);

        const applyCandles = (bars: RawBar[], spot: number) => {
            const cs = candleRef.current;
            if (!cs || !bars.length) return;
            // index symbols come back on the ETF scale: bring them onto the chain's price
            const lastClose = bars[bars.length - 1].c;
            const rel = spot > 0 ? lastClose / spot : 1;
            const k = spot > 0 && !(rel > 0.8 && rel < 1.25) ? spot / lastClose : 1;
            const r2 = (v: number) => Math.round(v * k * 100) / 100;
            const candles = bars.map((b) => ({
                time: (Math.floor(b.t / 1000) + off) as Time,
                open: r2(b.o),
                high: r2(b.h),
                low: r2(b.l),
                close: r2(b.c),
            }));
            cs.setData(candles);
            if (sma20Ref.current && sma50Ref.current) {
                const sma = (n: number) => {
                    const out: { time: Time; value: number }[] = [];
                    let sum = 0;
                    for (let i = 0; i < candles.length; i++) {
                        sum += candles[i].close;
                        if (i >= n) sum -= candles[i - n].close;
                        if (i >= n - 1) out.push({ time: candles[i].time, value: Math.round((sum / n) * 100) / 100 });
                    }
                    return out;
                };
                sma20Ref.current.setData(sma(20));
                sma50Ref.current.setData(sma(50));
            }
            if (ta) {
                const res = analyse(candles.map((c, i) => ({ time: c.time as number, open: c.open, high: c.high, low: c.low, close: c.close, volume: Number(bars[i].v) || 0 })));
                const lastT = candles[candles.length - 1].time as number;
                // trend lines: from their first swing out to the last bar
                res.lines.forEach((ln, k) => {
                    const s = trendRefs.current[k];
                    if (s) s.setData([{ time: ln.a.time as Time, value: Math.round(ln.a.price * 100) / 100 }, { time: lastT as Time, value: Math.round(ln.last * 100) / 100 }]);
                });
                for (let k = res.lines.length; k < trendRefs.current.length; k++) trendRefs.current[k].setData([]);
                trendRefs.current.forEach((s, k) => {
                    const ln = res.lines[k];
                    if (ln) s.applyOptions({ color: ln.kind === "resistance" ? "#f87171" : "#4ade80" });
                });
                candleMarksRef.current?.setMarkers(
                    res.candles.map((m) => ({
                        time: m.time as Time,
                        position: m.bullish ? "belowBar" : "aboveBar",
                        color: m.bullish ? "#4ade80" : "#f87171",
                        shape: m.bullish ? "arrowUp" : "arrowDown",
                        text: m.code,
                    })),
                );
                for (const pl of vpLinesRef.current) {
                    try {
                        cs.removePriceLine(pl);
                    } catch {
                        /* already gone */
                    }
                }
                vpLinesRef.current = [];
                if (res.profile) {
                    const mk = (price: number, title: string, color: string) =>
                        cs.createPriceLine({ price: Math.round(price * 100) / 100, color, lineWidth: 1, lineStyle: LineStyle.SparseDotted, axisLabelVisible: true, title });
                    vpLinesRef.current = [mk(res.profile.poc, "POC", "#fbbf24"), mk(res.profile.vah, "VAH", "#94a3b8"), mk(res.profile.val, "VAL", "#94a3b8")];
                }
                setTaRes(res);
                (window as any).__lgxTa = {
                    trend: res.trend,
                    pattern: res.pattern,
                    candles: res.candles.map((m) => m.name),
                    support: res.lines.find((l) => l.kind === "support")?.last ?? null,
                    resistance: res.lines.find((l) => l.kind === "resistance")?.last ?? null,
                    poc: res.profile?.poc ?? null,
                    vah: res.profile?.vah ?? null,
                    val: res.profile?.val ?? null,
                };
            }
            barTimesRef.current = new Set(candles.map((c) => c.time as number));
            const last = candles[candles.length - 1];
            const dayOf = (t: number) => Math.floor(t / 86400);
            let prevClose = 0;
            for (let i = candles.length - 1; i >= 0; i--) {
                if (dayOf(candles[i].time as number) !== dayOf(last.time as number)) {
                    prevClose = candles[i].close;
                    break;
                }
            }
            const chg = prevClose ? last.close - prevClose : 0;
            setLegend({ o: last.open, h: last.high, l: last.low, c: last.close, chg, chgPct: prevClose ? (chg / prevClose) * 100 : 0 });
            if (firstFitRef.current) {
                firstFitRef.current = false;
                const n = candles.length;
                chartRef.current?.timeScale().setVisibleLogicalRange({ from: Math.max(0, n - 150), to: n + 24 });
            }
        };

        const applyLevels = (lv: any, rtlRows: any[], spot: number, chain: ChainResult) => {
            clearLevels();
            const cs = candleRef.current;
            if (!cs || !lv) return;
            // "0D" today, "3D" for a weekly name mid-week, "ALL" for the aggregated weekly range
            primRef.current?.setOptions({ dteShort: chain.dte == null ? "ALL" : `${chain.dte}D` });
            setDteLabel(chain.dte == null ? "weekly" : `${chain.dte}DTE`);
            const lines = buildLines(lv, dteColor(chain.dte), spot);
            for (const l of lines) {
                priceLinesRef.current.push(
                    cs.createPriceLine({
                        price: l.price,
                        color: l.color,
                        lineWidth: l.width as any,
                        lineStyle: l.solid ? LineStyle.Solid : LineStyle.Dashed,
                        axisLabelVisible: true,
                        title: "",
                    }),
                );
            }
            if (lines.length) {
                const pad = spot * 0.0006;
                levelRangeRef.current = {
                    lo: Math.min(...lines.map((l) => l.price)) - pad,
                    hi: Math.max(...lines.map((l) => l.price)) + pad,
                };
            }
            // RTL net flow for the same expiry the levels were computed on
            const net = chain.expiryYmd ? rtlNetByStrike(rtlRows, chain.expiryYmd) : new Map<number, number>();
            const strikes: StrikeBar[] = (lv.strikes || []).map((s: any) => ({
                strike: Number(s.strike),
                gamma: Number(s.netGex) || 0,
                flow: net.get(Number(s.strike)) || 0,
            }));
            primRef.current?.setData(lines, strikes);
            setLevelKey(lines);
        };

        const applyIv = () => {
            if (!ivHist || !ivRef.current || !rvRef.current) return;
            const times = barTimesRef.current;
            // Snap samples onto the candle grid so this pane adds no points to the shared time scale.
            const snapTo = (arr: any[]) => {
                const m = new Map<number, number>();
                (ivHist.t || []).forEach((t: number, i: number) => {
                    const v = arr?.[i];
                    if (v == null) return;
                    const b = Math.floor(t / 300) * 300 + off;
                    if (times.has(b)) m.set(b, Number(v));
                });
                return Array.from(m.entries())
                    .sort((a, b) => a[0] - b[0])
                    .map(([time, value]) => ({ time: time as Time, value }));
            };
            ivRef.current.setData(snapTo(ivHist.iv));
            rvRef.current.setData(snapTo(ivHist.rv_intraday));
        };

        const run = async () => {
            try {
                // Requests go one at a time: the session gate caps concurrency per tab.
                const tv = await fetchIvSnapshot(symbol).catch(() => null);
                if (cancelled) return;
                if (tv) setSnap(tv);
                const bars = await fetchBars(symbol).catch(() => [] as RawBar[]);
                if (cancelled) return;

                let lv: any = null;
                let chk: SpotCheck | null = null;
                let rtl: any[] = [];
                let chain: ChainResult = { rows: [], expiryYmd: null, dte: null };
                try {
                    chain = await fetchChain(symbol, range);
                    const rows = chain.rows;
                    rtl = await fetchRtlRows(feed).catch(() => []);
                    attachAccumulatedVolume(rows, rtl);
                    lv = (processRowsByDTE(rows, ["all"]) as any[])[0]?.levels || null;
                    // No traded volume behind the chain → the volume-weighted levels mean nothing.
                    if (lv && !rows.some((r) => Number(r["Accumulated Volume"]) > 0)) {
                        lv.volGammaFlip = null;
                        lv.volMaxGex = null;
                        lv.volMinGex = null;
                    }
                    chk = lv ? spotCheck(rows, Number(lv.spot) || 0) : null;
                } catch (e) {
                    console.warn("[live-gex] chain unavailable:", e);
                }
                if (cancelled) return;
                // Market closed: the chain cannot be fresh, so its levels are shown as what they
                // are -- the last session's -- when the caller asked for that.
                const relaxed = sessionLevels && !!lv && !!chk && !chk.ok && !usMarketOpen();
                const trusted = !!lv && (!chk || chk.ok || relaxed);
                const spot = (trusted ? Number(lv.spot) : 0) || Number(tv?.spot) || 0;
                spotRef.current = spot;
                applyCandles(bars, spot);
                applyLevels(trusted ? lv : null, rtl, spot, chain);
                setGuard(relaxed ? null : chk);
                setLastSession(relaxed);
                setHasLevels(trusted);
                setMarks((m) => m.slice()); // redraw the marks over the fresh levels
                // Delta exposure of the open interest on this chain: sum of delta x OI x 100 x spot,
                // calls positive and puts negative as their deltas are.
                if (trusted && chain.rows.length && spot > 0) {
                    let d = 0;
                    for (const r of chain.rows) d += (Number(r["Delta"]) || 0) * (Number(r["Open Interest"]) || 0) * 100 * spot;
                    setDex(Number.isFinite(d) && d !== 0 ? d : null);
                } else setDex(null);

                const prints = await fetchZeroDteFlow(feed).catch(() => [] as FlowPrint[]);
                if (cancelled) return;
                setFlow(prints);

                if (cycle % 3 === 0 || !ivHist) {
                    const h = await fetchIvHistory(symbol).catch(() => null);
                    if (cancelled) return;
                    if (h) ivHist = h;
                }
                applyIv();
                setStatus(bars.length ? "live" : "error");
                setStamp(new Date().toLocaleTimeString("en-US", { timeZone: "America/New_York", hour12: false }));
            } catch (e) {
                console.warn("[live-gex] refresh failed:", e);
                if (!cancelled) setStatus("error");
            } finally {
                cycle++;
                if (!cancelled) timer = window.setTimeout(run, POLL_MS);
            }
        };
        run();
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [symbol, range]);

    // ── derived display values ───────────────────────────────────────
    const ds = isDesignSystemOn();
    const regimeKey = String(snap?.gex_regime || "").toLowerCase();
    const regime0 = REGIMES[regimeKey] || null;
    const regime = regime0 && ds && DS_REGIME[regimeKey] ? { ...regime0, ...DS_REGIME[regimeKey] } : regime0;
    const bn = snap?.net_gex_bn;
    const gex = bn == null ? "—" : `${Number(bn) >= 0 ? "+" : "−"}${Math.abs(Number(bn)).toFixed(2)}bn`;
    const iv = snap?.iv;
    const rv = snap?.rv_intraday;
    const ivRv = Number(rv) > 0 && iv != null ? (Number(iv) / Number(rv)).toFixed(2) : "—";
    const volLabel = snap?.hv20 != null && Number(iv) < Number(snap.hv20) ? "CALM" : "NORMAL";
    const up = (legend?.chg || 0) >= 0;
    const etNow = new Date().toLocaleTimeString("en-US", { timeZone: "America/New_York", hour12: false, hour: "2-digit", minute: "2-digit" });
    const railBtn = (on: boolean, label: string, onClick: () => void, key?: string) => (
        <button key={key || label} type="button" className={on ? "on" : ""} onClick={onClick} aria-pressed={on}>
            {label}
        </button>
    );

    return (
        <div className={`lgx-root${fill ? " lgx-fill" : ""}${ds ? " lgx-ds mmec" : ""}`}>
            <div className="lgx-stage">
                <div className="lgx-chart" ref={boxRef} />

                <div className="lgx-legend">
                    <span className="lgx-dot">{symbol.slice(0, 3)}</span>
                    <span className="lgx-nm">{NAMES[symbol] || symbol} · 5 · MMEC</span>
                    {legend && (
                        <span className="lgx-ohlc" style={{ color: up ? "var(--up-text, #26a69a)" : "var(--down-text, #ef5350)" }}>
                            O<b>{fmtPx(legend.o)}</b> H<b>{fmtPx(legend.h)}</b> L<b>{fmtPx(legend.l)}</b> C<b>{fmtPx(legend.c)}</b>{" "}
                            {legend.chg >= 0 ? "+" : ""}
                            {legend.chg.toFixed(2)} ({legend.chgPct >= 0 ? "+" : ""}
                            {legend.chgPct.toFixed(2)}%)
                        </span>
                    )}
                </div>
                {ta && vpBars.length > 0 && (
                    <div className="lgx-vp" aria-hidden>
                        {vpBars.map((b, k) => (
                            <i key={k} className={b.poc ? "poc" : b.va ? "va" : ""} style={{ top: b.top, height: b.h, width: b.w }} />
                        ))}
                    </div>
                )}
                {ta && taRes && (
                    <div className="lgx-ta" style={{ top: Math.max(60, dims.mainH - 92) }}>
                        <b>AUTO TA</b>
                        <span>
                            Trend <em className={taRes.trend}>{taRes.trend === "up" ? "Up" : taRes.trend === "down" ? "Down" : "Sideways"}</em>
                        </span>
                        <span>
                            Pattern <em>{taRes.pattern || "none"}</em>
                        </span>
                        <span>
                            Candles <em>{taRes.candles.length ? Array.from(new Set(taRes.candles.slice(-3).map((c) => c.name))).join(" · ") : "none"}</em>
                        </span>
                    </div>
                )}
                {lastSession && (
                    <div className="lgx-session" role="status">
                        Last session's levels · market closed
                    </div>
                )}
                {guard && !guard.ok && (
                    <div className="lgx-guard" role="status">
                        Levels withheld — the option chain's price is stale ({guard.reasons[0]}). Retrying…
                    </div>
                )}
                {status === "loading" && !legend && (ds ? (
                    <div className="lgx-wait lgx-ds-wait"><ToolState kind="loading" fill /></div>
                ) : <div className="lgx-wait">Loading {symbol}…</div>)}

                {showPanels && (
                    // capped to the price pane so the flow list never runs over the IV pane
                    <div className="lgx-panels" style={dims.mainH > 0 ? { maxHeight: Math.max(120, dims.mainH - 78) } : undefined}>
                        {ds && levelKey.length > 0 && (
                            <div className="lgx-panel lgx-ds-key">
                                <div className="lgx-ph">
                                    <span className="lgx-pt">LEVELS</span>
                                    <span className="lgx-ps">{dteLabel}</span>
                                </div>
                                <div className="lgx-pb">
                                    {levelKey.map((l) => (
                                        <div className="lgx-ds-keyrow" key={l.price}>
                                            <svg width="36" height="6" aria-hidden="true">
                                                <line x1="0" y1="3" x2="36" y2="3" stroke={l.color} strokeWidth="2" strokeDasharray={l.solid ? undefined : "8 6"} />
                                            </svg>
                                            <span>{l.abbr.join(" · ")}</span>
                                            <span className="lgx-ds-keypx">{fmtPx(l.price)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="lgx-panel">
                            <div className="lgx-ph">
                                <span className="lgx-pt">REGIME &amp; IV</span>
                                <span className="lgx-ps">
                                    {symbol} · {etNow} ET
                                </span>
                            </div>
                            <div className="lgx-pb">
                                {regime ? (
                                    <>
                                        <div className="lgx-rg">
                                            <span className="lgx-t" style={{ color: regime.color, background: regime.bg }}>
                                                {regime.title}
                                            </span>
                                            <span className="lgx-g">GEX {gex}</span>
                                            {dex != null && (
                                                <span className="lgx-g" title="Net delta of open interest, in dollars">
                                                    DEX {dex >= 0 ? "+" : "−"}
                                                    {Math.abs(dex / 1e9).toFixed(2)}bn
                                                </span>
                                            )}
                                        </div>
                                        <div className="lgx-hint">{regime.hint}</div>
                                    </>
                                ) : (
                                    <div className="lgx-hint">Regime unavailable.</div>
                                )}
                                <div className="lgx-sec">
                                    <span>VOLATILITY</span>
                                    <span style={{ color: "var(--info, #93c5fd)" }}>{volLabel}</span>
                                </div>
                                <div className="lgx-tiles">
                                    <div className="lgx-tile">
                                        <div className="lgx-k">IV rank</div>
                                        <div className="lgx-v">{fmt(snap?.iv_rank, 0)}</div>
                                    </div>
                                    <div className="lgx-tile">
                                        <div className="lgx-k">IV − HV20</div>
                                        <div className="lgx-v">{fmt(snap?.gap_hv20, 1)}</div>
                                    </div>
                                    <div className="lgx-tile">
                                        <div className="lgx-k">P(spike) 1d</div>
                                        <div className="lgx-v">
                                            {snap?.spike_prob == null ? "—" : `${Math.round(Number(snap.spike_prob) * 100)}%`}
                                        </div>
                                    </div>
                                </div>
                                <div className="lgx-kv">
                                    <span>Realized</span>
                                    <span>
                                        HV10 <b>{fmt(snap?.hv10, 1)}</b>
                                    </span>
                                    <span>
                                        HV20 <b>{fmt(snap?.hv20, 1)}</b>
                                    </span>
                                    <span>
                                        HV30 <b>{fmt(snap?.hv30, 1)}</b>
                                    </span>
                                </div>
                                <div className="lgx-kv">
                                    <span>Forecast</span>
                                    <span>
                                        1d <b>{fmt(snap?.fc_1d, 1)}</b>
                                    </span>
                                    <span>
                                        5d <b>{fmt(snap?.fc_5d, 1)}</b>
                                    </span>
                                    <span>
                                        22d <b>{fmt(snap?.fc_22d, 1)}</b>
                                    </span>
                                </div>
                                <div className="lgx-kv">
                                    <span>ATM IV</span>
                                    <span>
                                        <b>{fmt(iv, 2)}%</b>
                                    </span>
                                    <span>
                                        RV <b>{fmt(rv, 1)}</b>
                                    </span>
                                    <span />
                                </div>
                            </div>
                        </div>

                        {flow.length > 0 && (
                            <div className="lgx-panel lgx-flowpanel">
                                <div className="lgx-ph">
                                    <span className="lgx-pt">FLOW</span>
                                    <span className="lgx-tag">{flow.every((p) => p.dte === 0) ? "0DTE" : "ALL EXP"}</span>
                                    <span className="lgx-ps" style={{ marginLeft: "auto", fontSize: ds ? 10 : 9.5, color: "var(--text-muted, #8a87a0)" }}>
                                        {flow.length} largest prints
                                    </span>
                                </div>
                                {flow.map((p, i) => (
                                    <div className="lgx-fl" key={`${p.cp}${p.strike}${p.premium}${i}`}>
                                        <span className={`lgx-s ${p.side}`}>{p.side}</span>
                                        <span className="lgx-k" style={{ color: p.cp === "C" ? "var(--up-text, #34d399)" : "var(--down-text, #f87171)" }}>
                                            {p.cp}
                                        </span>
                                        <span className="lgx-k">{p.strike.toFixed(0)}</span>
                                        <span className="lgx-m">
                                            {p.dte === 0 ? "0DTE" : `${p.dte}d`} · {p.volume.toLocaleString("en-US")}x
                                        </span>
                                        <span className="lgx-p">${Math.round(p.premium / 1000).toLocaleString("en-US")}K</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {regime && dims.mainH > 0 && (
                    <div
                        className="lgx-ribbon"
                        style={{
                            top: dims.mainH - 22,
                            width: dims.plotW,
                            background: regime.ribbon,
                            color: regime.color,
                            paddingLeft: showPanels ? 312 : 10,
                        }}
                    >
                        <b>{regime.short}</b> {regime.who} · GEX {gex}
                    </div>
                )}
                {showProfiles && hasLevels && dims.mainH > 0 && (
                    <div className="lgx-cap" style={{ right: Math.max(8, dims.w - dims.plotW + 12), top: dims.mainH - 66 }}>
                        <div>
                            <i style={{ background: ds ? "#7986cbcc" : "rgba(96,165,250,0.78)" }} />
                            {symbol} Gamma · open interest
                        </div>
                        <div>
                            <i style={{ background: "#26a69a" }} />
                            {feedSymbol(symbol)} Net · accum · {dteLabel}
                        </div>
                    </div>
                )}
                {dims.mainH > 0 && (
                    <div className="lgx-ivh" style={{ top: dims.mainH + 8 }}>
                        MMEC IV · 5m
                        <span className="lgx-x">IV / RV {ivRv}×</span>
                        <span className="lgx-y">1.00× = IV equals RV</span>
                        <span className="lgx-z">above 1: options rich vs realized</span>
                    </div>
                )}
            </div>

            <div className="lgx-rail" role="toolbar" aria-label="Real-Time GEX controls">
                <span className="lgx-mm">MMEC · Real-Time GEX</span>
                <span className="lgx-st">
                    {status === "live" && (
                        <>
                            <span className="lgx-live">● LIVE</span> · {POLL_MS / 1000}s · {stamp} ET
                        </>
                    )}
                    {status === "loading" && "Loading…"}
                    {status === "error" && <span className="lgx-err">Feed unavailable — retrying</span>}
                </span>
                <span className="lgx-gl">SYMBOL</span>
                {SYMBOLS.map((s) => railBtn(symbol === s, s, () => setSymbol(s)))}
                <span className="lgx-gl">DTE</span>
                {railBtn(range === "0dte", "0DTE", () => setRange("0dte"))}
                {railBtn(range === "week", "Week", () => setRange("week"))}
                <span className="lgx-gl">LABELS</span>
                {(["L", "M", "R"] as const).map((a) => railBtn(align === a, a, () => setAlign(a), `al-${a}`))}
                <span className="lgx-gl">PROFILE</span>
                {railBtn(showProfiles, "On", () => setShowProfiles(true), "pf-on")}
                {railBtn(!showProfiles, "Off", () => setShowProfiles(false), "pf-off")}
                <span className="lgx-gl">PANELS</span>
                {railBtn(showPanels, "On", () => setShowPanels(true), "pn-on")}
                {railBtn(!showPanels, "Off", () => setShowPanels(false), "pn-off")}
            </div>
        </div>
    );
};

/**
 * Renders the workspace, and if it ever throws at runtime, the fallback instead — so a fault
 * in the new view can never take the rest of the app down with it.
 */
export class LiveGexBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
    state = { failed: false };

    static getDerivedStateFromError(): { failed: boolean } {
        return { failed: true };
    }

    componentDidCatch(error: unknown): void {
        console.error("[live-gex] workspace failed, showing the previous chart:", error);
    }

    render(): ReactNode {
        return this.state.failed ? this.props.fallback : this.props.children;
    }
}

export default LiveGexWorkspace;
